/**
 * The session, held in one place.
 *
 * Tokens are HttpOnly cookies set by the backend — this context never sees or
 * stores them, and there is deliberately nothing in localStorage. What it holds
 * is the USER object, which the shell needs (name, venues) and which no cookie
 * carries.
 *
 * Mount sequence is two calls, not one:
 *
 *   1. POST /api/auth/refresh  — is there a live session? Reissues the access
 *      token. Returns only { id, email, platformRole }; it is a token endpoint,
 *      not an identity one.
 *   2. GET  /api/auth/me       — the full user including the venues list.
 *
 * Step 2 exists because the sidebar cannot be drawn from step 1's payload. See
 * the note at the top of functions/api/auth/me.js.
 */

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { apiGet, apiPost, refreshSession } from '../lib/api.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  /** Pulls the full user. Returns the user or null; never throws. */
  const loadUser = useCallback(async () => {
    const { data } = await apiGet('/api/auth/me');
    const next = data?.user ?? null;
    setUser(next);
    return next;
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      // A failed refresh is the ordinary state for a signed-out visitor, not an
      // error worth surfacing — public pages render regardless.
      const alive = await refreshSession();

      if (!cancelled) {
        if (alive) await loadUser();
        // setIsLoading last: a route guard reading isAuthenticated before the
        // user has landed would bounce a signed-in user to /login.
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [loadUser]);

  const login = useCallback(async (email, password, rememberMe = false) => {
    const { data, error } = await apiPost('/api/auth/login', { email, password, rememberMe });

    if (error) return { ok: false, error };

    // login returns the same user shape as /api/auth/me, venues included, so
    // there is no follow-up call to make here.
    setUser(data.user);
    return { ok: true, user: data.user };
  }, []);

  /**
   * Self-service signup.
   *
   * POST /api/auth/register returns a LIVE SESSION — cookies set, the same user
   * shape login returns — so there is no follow-up login and no /me round trip,
   * for the same reason login has none. See functions/api/auth/register.js.
   *
   * The account works immediately. Nothing gates on the address having been
   * confirmed; what the verification email unlocks is claiming guest history.
   */
  const register = useCallback(async (email, password, displayName) => {
    const { data, error } = await apiPost('/api/auth/register', {
      email, password, displayName,
    });

    if (error) return { ok: false, error };

    setUser(data.user);
    return { ok: true, user: data.user };
  }, []);

  const logout = useCallback(async () => {
    // State is cleared whatever the server says. A client that cannot reach the
    // backend must still drop the session locally rather than sit there looking
    // signed in.
    try {
      await apiPost('/api/auth/logout');
    } finally {
      setUser(null);
    }
  }, []);

  /**
   * Adopts a user object an endpoint has already returned.
   *
   * For the writes that answer with the WHOLE session user — PUT /api/auth/me
   * is the one — where re-reading would be asking for something already in
   * hand. refresh() costs two round trips (a token reissue, then /me), which on
   * a settings page of toggles is twenty-odd requests to learn what each
   * response said.
   *
   * ONLY for responses in buildUserResponse's shape. A partial object written
   * here would silently drop fields the shell reads; anything that answers with
   * less than a whole user calls refresh() instead.
   */
  const applyUser = useCallback((next) => {
    if (next) setUser(next);
  }, []);

  /** Re-reads the session — after a role change, or a venue being approved. */
  const refresh = useCallback(async () => {
    const alive = await refreshSession();
    if (!alive) { setUser(null); return null; }
    return loadUser();
  }, [loadUser]);

  const value = {
    user,
    login,
    register,
    logout,
    refresh,
    applyUser,
    isAuthenticated: user !== null,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (ctx === null) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/**
 * The caller's role at a venue, by slug, from the venues list the session
 * already carries. Returns null when they have no role there.
 *
 * This is also how the frontend maps a :venueSlug URL to the numeric id the API
 * is keyed on, without a round trip. Public pages have no session and use
 * GET /api/venues/by-slug/:slug instead.
 */
export function useVenue(slug) {
  const { user } = useAuth();
  if (!user || !slug) return null;
  return user.venues.find((v) => v.slug === slug) ?? null;
}
